import { Vector } from './vector';
import { wallForce } from './geometry';
import type { Segment } from './geometry';
import type { BehaviorTag, FishOpts, Species } from './types';

export interface World {
    width: number;
    height: number;
}

const HALF_PI = Math.PI * 0.5;
const TWO_PI = Math.PI * 2;

let _showBehavior = false;
let _nextId = 0;
const _defaultRng = (): number => Math.random();
let _rng: () => number = _defaultRng;

export function setShowBehavior(value: boolean): void {
    _showBehavior = value;
}

export function getShowBehavior(): boolean {
    return _showBehavior;
}

export function setFishRng(rng: () => number): void {
    _rng = rng;
}

export function resetFishRng(): void {
    _rng = _defaultRng;
}

export function getFishRng(): () => number {
    return _rng;
}

export class Fish {
    readonly id: number;
    readonly species: Species | undefined;
    mass: number;
    maxspeed: number;
    maxforce: number;
    separationRange: number;
    lookRange: number;
    length: number;
    base: number;
    location: Vector;
    velocity: Vector;
    acceleration: Vector;
    wandering: Vector;
    baseColor: string;
    color: string;
    aggression: number;
    social: number;
    age: number;
    growthRate: number;
    massCap: number;
    currentBehavior: BehaviorTag;

    avoidList: Fish[] | null;
    chaseList: Fish[] | null;
    shoalList: Fish[] | null;

    constructor(opts: FishOpts, x: number, y: number) {
        this.id = _nextId++;
        this.mass = opts.mass;
        this.maxspeed = opts.maxspeed ?? 12 * opts.mass;
        this.maxforce = opts.maxforce ?? 0.1 / opts.mass;
        this.separationRange = opts.separationRange ?? opts.mass * 30;
        this.lookRange = opts.lookRange ?? opts.mass * 200;
        this.length = opts.mass * 20;
        this.base = this.length * 0.5;
        this.baseColor = opts.color ?? '#000000';
        this.color = this.baseColor;
        this.aggression = opts.aggression ?? 1;
        this.social = opts.social ?? 1;
        this.species = opts.species;
        this.age = 0;
        this.growthRate = opts.growthRate ?? 0;
        this.massCap = opts.massCap ?? opts.mass * 2;
        this.currentBehavior = 'wander';

        this.location = new Vector(x, y);
        this.velocity = new Vector(0, 0);
        this.acceleration = new Vector(0, 0);
        this.wandering = new Vector(0.2, 0.2);

        this.avoidList = null;
        this.chaseList = null;
        this.shoalList = null;
    }

    setMass(value: number): void {
        this.mass = value;
        this.length = value * 20;
        this.base = this.length * 0.5;
    }

    draw(ctx: CanvasRenderingContext2D): void {
        this.drawBehavior(ctx);

        const angle = this.velocity.angle();
        const x1 = this.location.x + Math.cos(angle) * this.base;
        const y1 = this.location.y + Math.sin(angle) * this.base;
        const xT = this.location.x - Math.cos(angle) * this.length;
        const yT = this.location.y - Math.sin(angle) * this.length;
        const x2 = this.location.x + Math.cos(angle + HALF_PI) * this.base;
        const y2 = this.location.y + Math.sin(angle + HALF_PI) * this.base;
        const x3 = this.location.x + Math.cos(angle - HALF_PI) * this.base;
        const y3 = this.location.y + Math.sin(angle - HALF_PI) * this.base;

        ctx.lineWidth = 2;
        ctx.fillStyle = this.color;
        ctx.strokeStyle = this.color;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(x2, y2, xT, yT);
        ctx.quadraticCurveTo(x3, y3, x1, y1);
        ctx.stroke();
        ctx.fill();
    }

    drawBehavior(ctx: CanvasRenderingContext2D): void {
        if (!_showBehavior) {
            this.color = this.baseColor;
            return;
        }

        const oldAlpha = ctx.globalAlpha;
        ctx.globalAlpha = 0.2;

        if (this.avoidList && this.avoidList.length > 0) {
            ctx.strokeStyle = 'blue';
            ctx.lineWidth = 4;
            ctx.beginPath();
            for (const o of this.avoidList) {
                ctx.moveTo(this.location.x, this.location.y);
                ctx.lineTo(o.location.x, o.location.y);
            }
            ctx.stroke();
        }

        if (this.chaseList && this.chaseList.length > 0) {
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;
            ctx.beginPath();
            for (const o of this.chaseList) {
                ctx.moveTo(this.location.x, this.location.y);
                ctx.lineTo(o.location.x, o.location.y);
            }
            ctx.stroke();
        }

        if (this.shoalList && this.shoalList.length > 0) {
            ctx.strokeStyle = 'black';
            ctx.lineWidth = 1;
            ctx.beginPath();
            for (const o of this.shoalList) {
                ctx.moveTo(this.location.x, this.location.y);
                ctx.lineTo(o.location.x, o.location.y);
            }
            ctx.stroke();
        }

        this.avoidList = null;
        this.chaseList = null;
        this.shoalList = null;
        ctx.globalAlpha = oldAlpha;
    }

    update(): void {
        this.velocity.add(this.acceleration);
        this.velocity.limit(this.maxspeed);
        if (this.velocity.mag() < 2) {
            this.velocity.setMag(Math.min(5, this.maxspeed));
        }
        this.location.add(this.velocity);
        this.acceleration.mul(0);
    }

    applyForce(f: Vector): void {
        this.acceleration.add(f);
    }

    boundaries(world: World, margin = 50, force = 3): void {
        if (this.location.x < margin) this.applyForce(new Vector(this.maxforce * force, 0));
        if (this.location.x > world.width - margin)
            this.applyForce(new Vector(-this.maxforce * force, 0));
        if (this.location.y < margin) this.applyForce(new Vector(0, this.maxforce * force));
        if (this.location.y > world.height - margin)
            this.applyForce(new Vector(0, -this.maxforce * force));
    }

    look(creatures: readonly Fish[], radius: number, angle: number): Fish[] {
        const neighbours: Fish[] = [];
        for (const other of creatures) {
            if (other === this) continue;

            const diff = this.location.copy().sub(other.location);
            const a = this.velocity.angleBetween(diff);
            const d = this.location.dist(other.location);
            if (d < radius && (a < angle / 2 || a > TWO_PI - angle / 2)) {
                neighbours.push(other);
            }
        }
        return neighbours;
    }

    wander(_radius: number, jitter = 0.05): void {
        if (_rng() < jitter) {
            this.wandering.rotate(TWO_PI * _rng());
        }
        this.velocity.add(this.wandering);
        this.currentBehavior = 'wander';
        if (_showBehavior) this.color = 'gray';
    }

    chase(creatures: readonly Fish[], aggression = 1): void {
        this.chaseList = [...creatures];
        if (creatures.length === 0) return;

        for (const o of creatures) {
            this.applyForce(o.attract(this, 50 * aggression));
        }
        this.currentBehavior = 'chase';
        if (_showBehavior) this.color = 'red';
    }

    follow(target: Vector, arrive: number): void {
        const dest = target.copy().sub(this.location);
        const d = dest.mag();
        if (d < arrive) dest.setMag((d / arrive) * this.maxspeed);
        else dest.setMag(this.maxspeed);
        this.applyForce(dest.limit(this.maxforce * 2));
        this.currentBehavior = 'follow';
    }

    seek(target: Vector): Vector {
        const out = target.copy().sub(this.location);
        out.normalize();
        out.mul(this.maxspeed);
        out.sub(this.velocity).limit(this.maxforce);
        return out;
    }

    attract(body: Fish, f: number): Vector {
        const force = this.location.copy().sub(body.location);
        let distance = force.mag();
        distance = distance < 5 ? 5 : distance > 25 ? 25 : distance;
        force.normalize();
        const strength = (f * this.mass * body.mass) / (distance * distance);
        force.mul(strength);
        return force;
    }

    separate(neighbours: readonly Fish[], range: number): Vector {
        const sum = new Vector(0, 0);
        if (neighbours.length === 0) return sum;

        for (const o of neighbours) {
            const d = this.location.dist(o.location);
            if (d > 0 && d < range) {
                const diff = this.location.copy().sub(o.location);
                diff.normalize();
                diff.div(d);
                sum.add(diff);
            }
        }
        sum.div(neighbours.length);
        sum.normalize();
        sum.mul(this.maxspeed);
        sum.sub(this.velocity);
        sum.limit(this.maxforce);
        return sum;
    }

    align(neighbours: readonly Fish[]): Vector {
        const sum = new Vector(0, 0);
        if (neighbours.length === 0) return sum;

        for (const o of neighbours) {
            sum.add(o.velocity);
        }
        sum.div(neighbours.length);
        sum.normalize();
        sum.mul(this.maxspeed);
        sum.sub(this.velocity).limit(this.maxspeed);
        return sum;
    }

    cohesion(neighbours: readonly Fish[]): Vector {
        const sum = new Vector(0, 0);
        if (neighbours.length === 0) return sum;

        for (const o of neighbours) {
            sum.add(o.location);
        }
        sum.div(neighbours.length);
        return this.seek(sum);
    }

    shoal(neighbours: readonly Fish[], weight = 1): void {
        this.shoalList = [...neighbours];
        const sep = this.separate(neighbours, this.separationRange).limit(this.maxforce);
        const ali = this.align(neighbours).limit(this.maxforce);
        const cohe = this.cohesion(neighbours).limit(this.maxforce);

        sep.mul(1.4 * weight);
        ali.mul(1.2 * weight);
        cohe.mul(1 * weight);

        this.applyForce(sep);
        this.applyForce(ali);
        this.applyForce(cohe);

        this.currentBehavior = 'shoal';
        if (_showBehavior) this.color = 'black';
    }

    avoidWalls(walls: readonly Segment[], range = 80, strength = 4): void {
        if (walls.length === 0) return;
        for (const seg of walls) {
            const force = wallForce(this.location, seg, {
                range,
                strength: strength * this.maxforce,
            });
            if (force.x !== 0 || force.y !== 0) {
                this.applyForce(force);
            }
        }
    }

    avoid(creatures: readonly Fish[], dist: number): void {
        this.avoidList = [...creatures];
        for (const o of creatures) {
            const d = this.location.dist(o.location);
            if (d < dist) {
                const v = o.location.copy().sub(this.location).mul(-100);
                this.applyForce(v);
            }
        }
        this.currentBehavior = 'flee';
        if (_showBehavior) this.color = 'blue';
    }
}
